/**
 * Partner List Page
 *
 * Displays filterable, paginated list of partner configurations.
 * Uses usePartnerData hook for clean separation of concerns.
 */

import React, { useCallback, useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Table, type Column, Pagination, type SortConfig } from '@/components/common/Table';
import { PartnerTypeBadge, PartnerStatusBadge } from '@/components/common/Badge';
import { PartnerFilters } from '@/components/partners/PartnerFilters';
import { BulkAssignOfferModal } from '@/components/partners/BulkAssignOfferModal';
import { usePartnerData } from '@/app/hooks/usePartnerData';
import { useHeaderActions } from '@/app/contexts/HeaderActionsContext';
import type {
  PartnerResponse,
  PartnerQueryParams,
  BulkAssignOfferResponse,
  BulkRemoveOfferResponse,
  BulkReplaceOfferResponse,
} from '@/app/types/partners';
import { formatDate } from '@/app/types/partners';
import { logger } from '@/app/utils/logger';
import { auditService } from '@/app/services';

import './PartnerList.scss';

const PartnerList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setActions } = useHeaderActions();

  const {
    partners,
    totalCount,
    currentPage,
    pageSize,
    totalPages,
    filters,
    isLoading,
    error,
    applyFilters,
    goToPage,
    changePageSize,
    resetFilters,
    refresh,
    pageSizeOptions,
  } = usePartnerData();

  // Set header actions
  useEffect(() => {
    setActions(
      <>
        <button className="btn-bulk-assign" onClick={() => setBulkAssignModalOpen(true)}>
          📦 Bulk Manage Offers
        </button>
        <button className="btn-refresh" onClick={() => applyFilters(filters)} disabled={isLoading}>
          ↻ Refresh
        </button>
        <button className="btn-create" onClick={() => navigate('/partners/new')}>
          + New Partner
        </button>
      </>
    );
    return () => setActions(null);
  }, [setActions, applyFilters, filters, isLoading, navigate]);

  // Route change detection - refetch when returning to list page
  const prevPathnameRef = React.useRef<string>('');
  useEffect(() => {
    const isReturningToList =
      location.pathname === '/partners' &&
      prevPathnameRef.current !== '/partners' &&
      prevPathnameRef.current !== '';

    if (isReturningToList && !isLoading) {
      refresh();
    }

    prevPathnameRef.current = location.pathname;
  }, [location.pathname, refresh, isLoading]);

  // Handle bulk operation success
  const handleBulkAssignSuccess = useCallback(
    (result: BulkAssignOfferResponse | BulkRemoveOfferResponse | BulkReplaceOfferResponse) => {
      logger.info('Bulk operation completed', { result });
      // Refresh the list
      applyFilters(filters);
    },
    [applyFilters, filters]
  );

  // Bulk assign modal state
  const [bulkAssignModalOpen, setBulkAssignModalOpen] = useState(false);

  // Sort state (client-side for now)
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: '', direction: null });

  // Handle filter changes (local state until search)
  const [localFilters, setLocalFilters] = useState<PartnerQueryParams>(filters);

  // Sync local filters when Redux filters change
  React.useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  // Sort partners client-side
  const sortedPartners = useMemo(() => {
    if (!sortConfig.direction || !sortConfig.key) return partners;

    const { key, direction } = sortConfig;

    return [...partners].sort((a, b) => {
      const aVal = a[key as keyof PartnerResponse];
      const bVal = b[key as keyof PartnerResponse];

      // Handle null/undefined
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return direction === 'asc' ? 1 : -1;
      if (bVal == null) return direction === 'asc' ? -1 : 1;

      // Handle numbers
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return direction === 'asc' ? aVal - bVal : bVal - aVal;
      }

      // Handle booleans
      if (typeof aVal === 'boolean' && typeof bVal === 'boolean') {
        const aNum = aVal ? 1 : 0;
        const bNum = bVal ? 1 : 0;
        return direction === 'asc' ? aNum - bNum : bNum - aNum;
      }

      // Convert to strings for comparison
      const aStr = String(aVal);
      const bStr = String(bVal);

      // Check if it's a date string
      if (key.includes('date') || key.includes('_at')) {
        const aDate = new Date(aStr).getTime();
        const bDate = new Date(bStr).getTime();
        if (!isNaN(aDate) && !isNaN(bDate)) {
          return direction === 'asc' ? aDate - bDate : bDate - aDate;
        }
      }

      // String comparison
      const result = aStr.localeCompare(bStr);
      return direction === 'asc' ? result : -result;
    });
  }, [partners, sortConfig]);

  const handleFiltersChange = useCallback((newFilters: PartnerQueryParams) => {
    setLocalFilters(newFilters);
  }, []);

  const handleSearch = useCallback(() => {
    applyFilters(localFilters);
  }, [applyFilters, localFilters]);

  // Log search audit after results return with accurate count
  const prevLoadingRef = React.useRef(isLoading);
  useEffect(() => {
    // Detect transition from loading to not loading (data just loaded)
    if (prevLoadingRef.current && !isLoading) {
      const filtersToLog: Record<string, unknown> = {};
      if (filters.search) filtersToLog.search = filters.search;
      if (filters.partner_type) filtersToLog.partner_type = filters.partner_type;
      if (filters.is_active !== undefined) filtersToLog.is_active = filters.is_active;

      auditService
        .logRecordSearch('partner', filtersToLog, totalCount, '/partners')
        .catch((err) => logger.warn('Failed to log record search', { error: err }));
    }
    prevLoadingRef.current = isLoading;
  }, [isLoading, totalCount, filters]);

  const handleClear = useCallback(() => {
    resetFilters();
    setLocalFilters({ limit: 10, offset: 0 });
  }, [resetFilters]);

  const handleRowClick = useCallback(
    (partner: PartnerResponse) => {
      navigate(`/partners/${partner.partner_product_id}`);
    },
    [navigate]
  );

  // Table columns
  const columns: Column<PartnerResponse>[] = [
    {
      key: 'partner_product_id',
      header: 'ID',
      width: '80px',
      sortable: true,
      render: (partner) => <span className="partner-id">{partner.partner_product_id}</span>,
    },
    {
      key: 'partner_name',
      header: 'Partner Name',
      width: '220px',
      sortable: true,
      render: (partner) => (
        <div className="name-cell">
          <span className="name">{partner.partner_name}</span>
        </div>
      ),
    },
    {
      key: 'doing_business_as',
      header: 'DBA',
      width: '180px',
      sortable: true,
      render: (partner) => <span className="dba">{partner.doing_business_as}</span>,
    },
    {
      key: 'partner_type',
      header: 'Type',
      width: '140px',
      sortable: true,
      render: (partner) => <PartnerTypeBadge type={partner.partner_type} />,
    },
    {
      key: 'is_active',
      header: 'Status',
      width: '100px',
      sortable: true,
      render: (partner) => (
        <PartnerStatusBadge
          isActive={partner.is_active}
          scheduledActivationAt={partner.scheduled_activation_at}
        />
      ),
    },
    {
      key: 'effective_date',
      header: 'Effective Date',
      width: '130px',
      sortable: true,
      render: (partner) => <span className="date">{formatDate(partner.effective_date)}</span>,
    },
    {
      key: 'actions',
      header: '',
      width: '80px',
      render: (partner) => (
        <button
          className="btn-view"
          onClick={(e) => {
            e.stopPropagation();
            handleRowClick(partner);
          }}
        >
          View
        </button>
      ),
    },
  ];

  return (
    <div className="partner-list-page">
      <PartnerFilters
        filters={localFilters}
        onFiltersChange={handleFiltersChange}
        onSearch={handleSearch}
        onClear={handleClear}
      />

      {error && (
        <div className="error-banner">
          <span>⚠️ {error}</span>
          <button onClick={handleSearch}>Retry</button>
        </div>
      )}

      <div className="results-summary">
        {!isLoading && (
          <span>
            {totalCount.toLocaleString()} partner{totalCount !== 1 ? 's' : ''} found
          </span>
        )}
      </div>

      <Table<PartnerResponse>
        data={sortedPartners}
        columns={columns}
        keyExtractor={(partner) => partner.partner_product_id.toString()}
        loading={isLoading}
        onRowClick={handleRowClick}
        emptyMessage="No partners found matching your criteria"
        sortConfig={sortConfig}
        onSortChange={setSortConfig}
      />

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalCount}
        itemsPerPage={pageSize}
        onPageChange={goToPage}
        pageSizeOptions={pageSizeOptions}
        onPageSizeChange={changePageSize}
      />

      {/* Bulk Assign Offer Modal (All Partners) */}
      <BulkAssignOfferModal
        isOpen={bulkAssignModalOpen}
        onClose={() => setBulkAssignModalOpen(false)}
        onSuccess={handleBulkAssignSuccess}
        partnerId={null}
        partnerName="All Partners"
      />
    </div>
  );
};

export default PartnerList;
